/**
 * Floating runes - SVG runic symbols drifting in the background.
 * Lightweight, purely decorative, auto-disables on mobile.
 */

const RUNE_SYMBOLS = [
  // Pentacle (etoile 5 branches)
  'M8,0 L10,6 L16,6 L11,10 L13,16 L8,12 L3,16 L5,10 L0,6 L6,6Z',
  // Spirale magique
  'M8,8 A1,1 0 0,1 9,7 A2,2 0 0,1 10,9 A3,3 0 0,1 7,11 A4,4 0 0,1 4,7 A5,5 0 0,1 9,3 A6,6 0 0,1 14,9',
  // Cercle + croix interieure (symbole d'invocation)
  'M8,0 A8,8 0 1,1 8,16 A8,8 0 1,1 8,0 M8,3 L8,13 M3,8 L13,8',
  // Croissant de lune
  'M10,1 A7,7 0 1,1 10,15 A5,5 0 1,0 10,1',
  // Oeil magique (oeil qui voit tout)
  'M0,8 Q8,0 16,8 Q8,16 0,8 M8,5 A3,3 0 1,1 8,11 A3,3 0 1,1 8,5',
  // Triangle encercle (alchimie)
  'M8,0 A8,8 0 1,1 8,16 A8,8 0 1,1 8,0 M8,3 L14,13 L2,13Z',
  // Etoile 6 branches (sceau)
  'M8,0 L10,5 L16,5 L11,9 L13,15 L8,11 L3,15 L5,9 L0,5 L6,5Z M8,2 L6,6 L2,7 L5,10 L4,14 L8,12 L12,14 L11,10 L14,7 L10,6Z',
  // Infini (boucle eternelle)
  'M3,8 C3,4 6,4 8,8 C10,12 13,12 13,8 C13,4 10,4 8,8 C6,12 3,12 3,8',
];

interface Rune {
  el: SVGElement;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  alpha: number;
}

export function initRunes(container: HTMLElement): () => void {
  if (window.innerWidth < 768) return () => {};

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'runes-layer');
  svg.setAttribute('aria-hidden', 'true');
  container.appendChild(svg);

  const runes: Rune[] = [];
  const COUNT = 12;
  let running = true;
  let raf = 0;

  function createRune(): Rune {
    const symbol =
      RUNE_SYMBOLS[Math.floor(Math.random() * RUNE_SYMBOLS.length)] ?? RUNE_SYMBOLS[0] ?? '';
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', symbol);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'rgba(201, 168, 76, 0.4)');
    path.setAttribute('stroke-width', '1');
    g.appendChild(path);
    svg.appendChild(g);

    return {
      el: g,
      x: Math.random() * 100,
      y: Math.random() * 100,
      vx: (Math.random() - 0.5) * 0.02,
      vy: -0.015 - Math.random() * 0.025,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 0.4,
      alpha: 0.15 + Math.random() * 0.25,
    };
  }

  for (let i = 0; i < COUNT; i++) {
    runes.push(createRune());
  }

  function resize() {
    const w = container.offsetWidth;
    const h = container.offsetHeight;
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    svg.style.width = `${w}px`;
    svg.style.height = `${h}px`;
  }

  function tick() {
    if (!running) return;
    const w = container.offsetWidth;
    const h = container.offsetHeight;

    for (const rune of runes) {
      rune.x += rune.vx;
      rune.y += rune.vy;
      rune.rotation += rune.rotationSpeed;

      if (rune.y < -5) {
        rune.y = 105;
        rune.x = Math.random() * 100;
      }
      if (rune.x < -5) rune.x = 105;
      if (rune.x > 105) rune.x = -5;

      const px = (rune.x / 100) * w;
      const py = (rune.y / 100) * h;
      rune.el.setAttribute(
        'transform',
        `translate(${px}, ${py}) rotate(${rune.rotation}) scale(3)`,
      );
      rune.el.setAttribute('opacity', String(rune.alpha));
    }

    raf = requestAnimationFrame(tick);
  }

  function onVisibility() {
    if (document.hidden) {
      cancelAnimationFrame(raf);
    } else {
      raf = requestAnimationFrame(tick);
    }
  }

  resize();
  window.addEventListener('resize', resize);
  document.addEventListener('visibilitychange', onVisibility);
  tick();

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
    document.removeEventListener('visibilitychange', onVisibility);
    svg.remove();
  };
}
